import React from 'react';
import { Box, Button, Dialog, DialogContent, Divider, Grid, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from 'components/common/IconButton';
import { useTranslation } from 'i18n';
import OrgsBox from 'components/common/EventCard/OrgsBox';

interface EventsDialogProps {
  open: boolean;
  onClose: () => void;
  moreDate: Date | null;
  moreEvents: any[];
  onGetTicketsClick: (eventId: number) => void;
  isMobile: boolean;
}

const EventsDialog: React.FC<EventsDialogProps> = ({
  open,
  onClose,
  moreDate,
  moreEvents,
  onGetTicketsClick,
  isMobile,
}) => {
  const [t] = useTranslation('public');

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '685px',
          borderRadius: '16px',
          padding: '40px 64px',
        },
      }}
    >
      <Grid container justifyContent="space-between" alignItems="start" mb={2}>
        <Grid item>
          <Typography fontSize={28} fontWeight={700}>
            {moreDate?.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
          <Typography color="text.secondary">
            {moreEvents.length} event{moreEvents.length > 1 ? 's' : ''}
          </Typography>
        </Grid>

        <Grid
          item
          sx={{
            position: 'relative',
            top: '-20px',
            right: '-38px',
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              width: 24,
              height: 24,
              padding: 0,
            }}
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Grid>
      </Grid>

      <DialogContent
        sx={{
          maxHeight: 500,
          overflowY: 'auto',
          padding: 0,
        }}
      >
        {moreEvents.map((event: any, index: number) => (
          <React.Fragment key={event.id}>
            <div style={{ paddingRight: 4 }}>
              <Grid
                container
                spacing={2}
                alignItems="flex-start"
                sx={{
                  py: 2,
                  px: 1,
                }}
              >
                <Grid item>
                  <Box
                    component="img"
                    src={event.main_image || '/placeholder.jpg'}
                    sx={{
                      width: 104,
                      height: 68,
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                  />
                </Grid>

                <Grid
                  item
                  xs
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minHeight: 80,
                  }}
                >
                  <Typography fontSize={13} fontWeight={500} color="textPrimary" component="div">
                    {new Date(event.start_at).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Typography>

                  <Typography fontSize={15} fontWeight={600} mb={0.5} component="div" lineHeight="18px">
                    {event.title}
                  </Typography>

                  <div>
                    <Typography variant="caption" color="textSecondary" fontWeight={500}>
                      {t('common:general.hostedBy')}
                    </Typography>

                    <Typography variant="caption" color="primary" style={{ fontWeight: 500 }}>
                      <OrgsBox
                        eventId={event.id}
                        orgs={[
                          ...(event.organization
                            ? [
                                {
                                  id: event.organization.id,
                                  title: event.organization.title,
                                },
                              ]
                            : []),
                          ...(event.organization_mentions ?? []).map((org: any) => ({
                            id: org.organization?.id,
                            title: org.organization?.title,
                          })),
                        ]}
                      />
                    </Typography>
                  </div>
                </Grid>

                {event.is_public && (
                  <Grid
                    item
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 80,
                    }}
                  >
                    <Button
                      variant="outlined"
                      sx={{
                        borderRadius: '16px',
                        height: '32px',
                        px: 3,
                        textTransform: 'uppercase',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                      onClick={() => {
                        onClose();
                        onGetTicketsClick(event.id);
                      }}
                    >
                      {event.cta || 'Get Tickets'}
                    </Button>
                  </Grid>
                )}
              </Grid>

              {index !== moreEvents.length - 1 && <Divider />}
            </div>
          </React.Fragment>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default EventsDialog;
